'use client'

import { memo } from 'react'
import { cn } from '@/lib/tailwind'
import { Button } from '@/ui/button'
import { Skeleton } from '@/ui/skeleton'
import { usePaginatedFeed } from './use-paginated-feed'
import { VirtualFeed } from './virtual-feed'
import type { FeedEntry } from './types'

interface PaginatedFeedProps {
  entries: FeedEntry[]
  renderItem: (entry: FeedEntry, index: number) => React.ReactNode
  estimatedItemHeight?: number
  className?: string
  emptyState?: React.ReactNode
}

export const PaginatedFeed = memo(function PaginatedFeed({
  entries,
  renderItem,
  estimatedItemHeight = 48,
  className,
  emptyState,
}: PaginatedFeedProps) {
  const {
    visibleItems,
    hasMore,
    isAutoLoading,
    loadMore,
    loadMoreRef,
    totalCount,
    displayedCount,
  } = usePaginatedFeed({ items: entries })

  if (entries.length === 0 && emptyState) {
    return <>{emptyState}</>
  }

  return (
    <div className={cn('flex flex-col', className)}>
      {/* Virtualized feed for visible items */}
      <VirtualFeed
        entries={visibleItems}
        renderItem={renderItem}
        estimatedItemHeight={estimatedItemHeight}
        className="flex-1"
      />

      {/* Auto-load sentinel (invisible element) */}
      {isAutoLoading && (
        <div ref={loadMoreRef} className="h-1" aria-hidden="true" />
      )}

      {/* Loading skeleton during auto-load */}
      {isAutoLoading && hasMore && (
        <div className="space-y-2 p-4">
          <Skeleton className="h-16 w-full rounded-lg" />
          <Skeleton className="h-16 w-full rounded-lg" />
        </div>
      )}

      {/* Manual Load More button after auto-load threshold */}
      {!isAutoLoading && hasMore && (
        <div className="flex justify-center py-4">
          <Button
            variant="outline"
            onClick={loadMore}
            className="min-h-[44px] px-6"
          >
            Load More ({displayedCount} of {totalCount})
          </Button>
        </div>
      )}

      {/* Optional: Show when all items loaded */}
      {!hasMore && totalCount > 0 && (
        <p className="text-muted-foreground py-4 text-center text-xs">
          Showing all {totalCount} items
        </p>
      )}
    </div>
  )
})
